/**
 * Hook that watches for credit_exhausted events from async AI actions
 * and opens the CreditExhaustedModal when a new one is detected.
 *
 * Subscribes to the latest credit_exhausted event for the current user.
 * On mount, captures the current event timestamp without triggering the modal
 * (to avoid showing stale events from before the page load). Only opens the
 * modal when a NEW event appears after initialization.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useCreditExhaustedModal } from "@/components/ai/credit-exhausted-modal-store";

export function useCreditExhaustionWatcher() {
  const latestExhaustion = useQuery(
    api.ai_credit_queries.getLatestCreditExhaustion,
    {},
  );
  const { open } = useCreditExhaustedModal();

  // Track whether we've initialized with the first query result.
  // On mount, we capture the current event's _creationTime so we don't
  // open the modal for pre-existing events from before this page load.
  const initializedRef = useRef(false);
  const lastSeenTimestamp = useRef<number | null>(null);

  useEffect(() => {
    if (latestExhaustion === undefined) return; // query still loading

    const timestamp = latestExhaustion?._creationTime ?? null;

    if (!initializedRef.current) {
      // First result after mount -- store it but don't trigger the modal.
      // This prevents opening for stale events from before the page load.
      initializedRef.current = true;
      lastSeenTimestamp.current = timestamp;
      return;
    }

    // Only trigger on NEW events (timestamp changed since last seen)
    if (
      timestamp !== null &&
      timestamp !== lastSeenTimestamp.current &&
      latestExhaustion
    ) {
      lastSeenTimestamp.current = timestamp;
      open(latestExhaustion.operation);
    }
  }, [latestExhaustion, open]);
}
